/* eslint-disable */
import React, { useEffect, useState, useCallback } from 'react';
import { getProjects, getUsers, createProject, updateProject, deleteProject, getNextStepTemplates } from '../../services/api';
import { NextStepTemplatesDialog } from '../../components/NextStepTemplatesDialog';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  FolderOpen,
  Plus,
  Edit,
  Trash2,
  TrendingUp,
  Users as UsersIcon,
  Briefcase,
  CheckCircle,
  Search,
  Sparkles,
  Map as MapIcon
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { PROJECT_STATUS_ORDER, PROJECT_DONE_STATUSES, projectStatus, projectStatusLabel } from '../../lib/workStatus';

const ProjectsManagement = () => {
  const { t } = useLanguage();
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    client_id: '',
    property_name: '',
    property_address: '',
    cadastral_number: '',
    status: 'not_started',
    next_step: '',
    area_ha: '',
    latitude: '',
    longitude: ''
  });
  const navigate = useNavigate();
  const [templates, setTemplates] = useState({});
  const [templatesOpen, setTemplatesOpen] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [projectsData, usersData] = await Promise.all([
        getProjects(),
        getUsers()
      ]);
      setProjects(projectsData);
      setClients(usersData.filter(u => u.role === 'client'));
      getNextStepTemplates().then((data) => setTemplates(data.templates || {})).catch(() => {});
    } catch (error) {
      toast.error(t('portalUi.admin.projects.toastLoadError', 'Neizdevās ielādēt projektus'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleOpenModal = (project = null) => {
    if (project) {
      setEditingProject(project);
      setFormData({
        name: project.name,
        description: project.description || '',
        client_id: project.client_id,
        property_name: project.property_name || '',
        property_address: project.property_address || '',
        cadastral_number: project.cadastral_number || '',
        status: project.status === 'active' ? 'in_progress' : project.status,
        next_step: project.next_step || '',
        area_ha: project.area_ha ?? '',
        latitude: project.latitude ?? '',
        longitude: project.longitude ?? ''
      });
    } else {
      setEditingProject(null);
      setFormData({
        name: '',
        description: '',
        client_id: '',
        property_name: '',
        property_address: '',
        cadastral_number: '',
        status: 'not_started',
        next_step: '',
        area_ha: '',
        latitude: '',
        longitude: ''
      });
    }
    setIsModalOpen(true);
  };

  const handleStatusChange = (value) => {
    setFormData((prev) => {
      const template = (templates[value] || '').trim();
      const current = (prev.next_step || '').trim();
      const isTemplate = !current || Object.values(templates).some((tpl) => (tpl || '').trim() === current);
      const unchangedStatus = editingProject && value === editingProject.status;
      return { ...prev, status: value, next_step: template && isTemplate && !unchangedStatus ? template : prev.next_step };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...formData,
      area_ha: formData.area_ha === '' ? null : Number(formData.area_ha),
      latitude: formData.latitude === '' ? null : Number(formData.latitude),
      longitude: formData.longitude === '' ? null : Number(formData.longitude)
    };
    try {
      if (editingProject) {
        await updateProject(editingProject.id, payload);
        toast.success(t('portalUi.admin.projects.toastUpdated', 'Projekts atjaunināts'));
      } else {
        await createProject(payload);
        toast.success(t('portalUi.admin.projects.toastCreated', 'Projekts izveidots'));
      }
      setIsModalOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('portalUi.admin.projects.toastError', 'Kļūda'));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('portalUi.admin.projects.confirmDelete', 'Vai tiešām vēlaties dzēst šo projektu?'))) return;
    
    try {
      await deleteProject(id);
      toast.success(t('portalUi.admin.projects.toastDeleted', 'Projekts dzēsts'));
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('portalUi.admin.projects.toastDeleteError', 'Neizdevās dzēst projektu'));
    }
  };

  const filteredProjects = projects.filter(p =>
    p.name.toLowerCase().includes(search.toLowerCase()) ||
    p.property_name?.toLowerCase().includes(search.toLowerCase())
  );

  const statusBadge = (status) => `${projectStatus(status).className} ${projectStatus(status).border}`;

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="w-8 h-8 border-4 border-[#1F3D2B] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#2E2E2E] flex items-center gap-3" data-testid="admin-projects-title">
            <FolderOpen className="w-8 h-8 text-[#1F3D2B]" />
            {t('portalUi.admin.projects.title', 'Projektu pārvaldība')}
          </h1>
          <p className="text-[#2E2E2E]/60 mt-1" data-testid="admin-projects-subtitle">{t('portalUi.admin.projects.subtitle', 'Pārvaldiet klientu projektus un darbus')}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => navigate('/portal/admin/map')} data-testid="admin-projects-open-map">
            <MapIcon className="w-4 h-4 mr-2" /> Karte
          </Button>
          <Button variant="outline" onClick={() => setTemplatesOpen(true)} data-testid="admin-projects-templates">
            <Sparkles className="w-4 h-4 mr-2" /> Veidnes “Kas notiks tālāk?”
          </Button>
          <Button
            onClick={() => handleOpenModal()}
            className="bg-[#1F3D2B] hover:bg-[#2a5039]"
            data-testid="admin-projects-add"
          >
            <Plus className="w-4 h-4 mr-2" />
            {t('portalUi.admin.projects.addButton', 'Jauns projekts')}
          </Button>
        </div>
      </div>

      <NextStepTemplatesDialog open={templatesOpen} onOpenChange={setTemplatesOpen} t={t} onSaved={setTemplates} />

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
        <Input
          placeholder={t('portalUi.admin.projects.searchPlaceholder', 'Meklēt projektus...')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
          data-testid="admin-projects-search"
        />
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#2E2E2E]/60">{t('portalUi.admin.projects.stats.total', 'Kopā projekti')}</p>
                <p className="text-3xl font-bold text-[#2E2E2E]">{projects.length}</p>
              </div>
              <FolderOpen className="w-8 h-8 text-[#1F3D2B]/20" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#2E2E2E]/60">{t('portalUi.admin.projects.stats.active', 'Procesā')}</p>
                <p className="text-3xl font-bold text-amber-600" data-testid="admin-projects-stat-active">
                  {projects.filter(p => p.status === 'in_progress' || p.status === 'active').length}
                </p>
              </div>
              <TrendingUp className="w-8 h-8 text-amber-600/20" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#2E2E2E]/60">{t('portalUi.admin.projects.stats.completed', 'Pabeigti')}</p>
                <p className="text-3xl font-bold text-green-600" data-testid="admin-projects-stat-completed">
                  {projects.filter(p => PROJECT_DONE_STATUSES.includes(p.status)).length}
                </p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-600/20" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#2E2E2E]/60">{t('portalUi.admin.projects.stats.totalJobs', 'Kopā darbi')}</p>
                <p className="text-3xl font-bold text-[#2E2E2E]">
                  {projects.reduce((sum, p) => sum + (p.total_jobs || 0), 0)}
                </p>
              </div>
              <Briefcase className="w-8 h-8 text-[#1F3D2B]/20" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Projects List */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {filteredProjects.map((project, index) => {
          const client = clients.find(c => c.id === project.client_id);
          return (
            <Card key={project.id} className="hover-lift card-shine hover:shadow-2xl transition-all cursor-pointer border-2 hover:border-[#1F3D2B]/20 animate-fade-in relative overflow-hidden"
              style={{ animationDelay: `${index * 0.1}s` }}
              onClick={() => navigate(`/portal/admin/projects/${project.id}`)}
            >
              {/* Decorative corner */}
              <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-[#1F3D2B]/10 to-transparent rounded-bl-full" />
              <div className="absolute bottom-0 left-0 w-16 h-16 bg-gradient-to-tr from-green-500/5 to-transparent rounded-tr-full" />
              <CardHeader className="relative">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-lg mb-2 group-hover:text-[#1F3D2B] transition-colors">{project.name}</CardTitle>
                    <div className="flex items-center gap-2 text-sm text-[#2E2E2E]/60">
                      <UsersIcon className="w-4 h-4 animate-float" />
                      {client?.client_name || t('portalUi.admin.projects.noClient', 'Nav norādīts')}
                    </div>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium border ${statusBadge(project.status)} shadow-sm`} data-testid={`admin-project-status-${project.id}`}>
                    {projectStatusLabel(t, project.status)}
                  </span>
                </div>
              </CardHeader>
              <CardContent className="relative">
                {project.property_name && (
                  <p className="text-sm text-[#2E2E2E]/70 mb-3 flex items-center gap-2">
                    <span className="text-base">🏞️</span>
                    {project.property_name}
                    {project.cadastral_number && ` • ${project.cadastral_number}`}
                  </p>
                )}
                
                {/* Progress Bar with Shimmer */}
                <div className="mb-4">
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-[#2E2E2E]/60">{t('portalUi.admin.projects.progressLabel', 'Progress')}</span>
                    <span className="font-semibold text-[#1F3D2B]">{project.progress}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                    <div
                      className="h-2 rounded-full transition-all duration-700 relative"
                      style={{ 
                        width: `${project.progress}%`,
                        background: 'linear-gradient(90deg, #1F3D2B, #2a5039, #22c55e)'
                      }}
                    >
                      <div className="absolute inset-0 opacity-50" style={{
                        background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)',
                        backgroundSize: '200% 100%',
                        animation: 'shimmer 2s infinite'
                      }} />
                    </div>
                  </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-4 gap-2 text-center">
                  <div>
                    <p className="text-xs text-[#2E2E2E]/60">{t('portalUi.admin.projects.jobsTotal', 'Kopā')}</p>
                    <p className="text-lg font-bold text-[#2E2E2E]">{project.total_jobs}</p>
                  </div>
                  <div>
                    <p className="text-xs text-[#2E2E2E]/60">{t('portalUi.admin.projects.jobsCompleted', 'Pabeigti')}</p>
                    <p className="text-lg font-bold text-green-600">{project.completed_jobs}</p>
                  </div>
                  <div>
                    <p className="text-xs text-[#2E2E2E]/60">{t('portalUi.admin.projects.jobsInProgress', 'Procesā')}</p>
                    <p className="text-lg font-bold text-amber-600">{project.in_progress_jobs}</p>
                  </div>
                  <div>
                    <p className="text-xs text-[#2E2E2E]/60">{t('portalUi.admin.projects.jobsPlanned', 'Plānoti')}</p>
                    <p className="text-lg font-bold text-blue-600">{project.planned_jobs}</p>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-2 mt-4 pt-4 border-t" onClick={(e) => e.stopPropagation()}>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleOpenModal(project)}
                    data-testid={`admin-projects-edit-${project.id}`}
                  >
                    <Edit className="w-4 h-4 mr-1" />
                    {t('portalUi.admin.projects.editButton', 'Rediģēt')}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    onClick={() => handleDelete(project.id)}
                    data-testid={`admin-projects-delete-${project.id}`}
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    {t('portalUi.admin.projects.deleteButton', 'Dzēst')}
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {filteredProjects.length === 0 && (
        <div className="text-center py-12 text-[#2E2E2E]/60" data-testid="admin-projects-empty">
          <FolderOpen className="w-16 h-16 mx-auto mb-4 opacity-20" />
          <p>{t('portalUi.admin.projects.emptyState', 'Nav atrasts neviens projekts')}</p>
        </div>
      )}

      {/* Create/Edit Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle data-testid="admin-projects-modal-title">
              {editingProject ? t('portalUi.admin.projects.modal.editTitle', 'Rediģēt projektu') : t('portalUi.admin.projects.modal.addTitle', 'Jauns projekts')}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="admin-projects-form">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>{t('portalUi.admin.projects.modal.nameLabel', 'Projekta nosaukums')} *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder={t('portalUi.admin.projects.modal.namePlaceholder', 'Īpašums Lejasrunķi - Mežsaimniecības darbi 2026')}
                  required
                  data-testid="admin-projects-name"
                />
              </div>
              
              <div className="col-span-2">
                <Label>{t('portalUi.admin.projects.modal.clientLabel', 'Klients')} *</Label>
                <Select
                  value={formData.client_id}
                  onValueChange={(value) => setFormData({ ...formData, client_id: value })}
                >
                  <SelectTrigger data-testid="admin-projects-client">
                    <SelectValue placeholder={t('portalUi.admin.projects.modal.clientPlaceholder', 'Izvēlieties klientu')} />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map(client => (
                      <SelectItem key={client.id} value={client.id}>
                        {client.client_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>{t('portalUi.admin.projects.modal.propertyNameLabel', 'Īpašuma nosaukums')}</Label>
                <Input
                  value={formData.property_name}
                  onChange={(e) => setFormData({ ...formData, property_name: e.target.value })}
                  placeholder={t('portalUi.admin.projects.modal.propertyNamePlaceholder', 'Lejasrunķi')}
                  data-testid="admin-projects-property-name"
                />
              </div>

              <div>
                <Label>{t('portalUi.admin.projects.modal.cadastralLabel', 'Kadastra apzīmējums')}</Label>
                <Input
                  value={formData.cadastral_number}
                  onChange={(e) => setFormData({ ...formData, cadastral_number: e.target.value })}
                  placeholder="8076 003 0123"
                  data-testid="admin-projects-cadastral"
                />
              </div>

              <div>
                <Label>Kopējā platība (ha)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.area_ha}
                  onChange={(e) => setFormData({ ...formData, area_ha: e.target.value })}
                  placeholder="12.40"
                  data-testid="admin-projects-area"
                />
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>Platums (lat)</Label>
                  <Input type="number" step="0.000001" value={formData.latitude} onChange={(e) => setFormData({ ...formData, latitude: e.target.value })} placeholder="56.9496" data-testid="admin-projects-lat" />
                </div>
                <div>
                  <Label>Garums (lng)</Label>
                  <Input type="number" step="0.000001" value={formData.longitude} onChange={(e) => setFormData({ ...formData, longitude: e.target.value })} placeholder="24.1052" data-testid="admin-projects-lng" />
                </div>
              </div>

              <div className="col-span-2">
                <Label>{t('portalUi.admin.projects.modal.addressLabel', 'Īpašuma adrese')}</Label>
                <Input
                  value={formData.property_address}
                  onChange={(e) => setFormData({ ...formData, property_address: e.target.value })}
                  placeholder={t('portalUi.admin.projects.modal.addressPlaceholder', 'Ropažu novads')}
                  data-testid="admin-projects-address"
                />
              </div>

              <div className="col-span-2">
                <Label>{t('portalUi.admin.projects.modal.descriptionLabel', 'Apraksts')}</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder={t('portalUi.admin.projects.modal.descriptionPlaceholder', 'Projekta detalizēts apraksts...')}
                  rows={3}
                  data-testid="admin-projects-description"
                />
              </div>

              <div>
                <Label>{t('portalUi.admin.projects.modal.statusLabel', 'Statuss')}</Label>
                <Select
                  value={formData.status}
                  onValueChange={handleStatusChange}
                >
                  <SelectTrigger data-testid="admin-projects-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[...PROJECT_STATUS_ORDER, 'on_hold'].map((value) => (
                      <SelectItem key={value} value={value} data-testid={`admin-projects-status-${value}`}>{projectStatusLabel(t, value)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="col-span-2">
                <Label>{t('portalUi.admin.projects.modal.nextStepLabel', 'Kas notiks tālāk?')}</Label>
                <Input
                  value={formData.next_step}
                  onChange={(e) => setFormData({ ...formData, next_step: e.target.value })}
                  placeholder={t('portalUi.admin.projects.modal.nextStepPlaceholder', 'Pabeigt cirsmas stigošanu')}
                  data-testid="admin-project-next-step-input"
                />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)} data-testid="admin-projects-cancel">
                {t('portalUi.admin.projects.modal.cancel', 'Atcelt')}
              </Button>
              <Button type="submit" className="bg-[#1F3D2B] hover:bg-[#2a5039]" data-testid="admin-projects-submit">
                {editingProject ? t('portalUi.admin.projects.modal.save', 'Saglabāt') : t('portalUi.admin.projects.modal.create', 'Izveidot')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProjectsManagement;
